import React, { useState, useRef, useCallback } from 'react';
import { useAuth, useUser as useClerkUser, SignUpButton } from '@clerk/react';
import { useUser } from '../contexts/UserContext';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Upload, Copy, Check, FileText, AlertCircle, ArrowRight, Loader2, X, Clock, ExternalLink, Info, Lock, Tag, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Upload states
const STATE = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  LIMIT_REACHED: 'limit_reached',
  ERROR: 'error',
};

// Max upload size (MB) per plan tier — mirrors the server-side per-plan limits
const PLAN_MAX_FILE_SIZE_MB = { free: 5, starter: 16, pro: 32, business: 100 };

export default function HeroUpload({ source }) {
  const { getToken } = useAuth();
  const { isSignedIn } = useClerkUser();
  const { userData } = useUser();

  const [uploadState, setUploadState] = useState(STATE.IDLE);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [resultUrl, setResultUrl] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [retentionDays, setRetentionDays] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expiryDays, setExpiryDays] = useState('1');

  const [customFilename, setCustomFilename] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [renamedNotice, setRenamedNotice] = useState('');

  const fileInputRef = useRef(null);

  const userPlan = userData?.user?.plan || userData?.plan || 'free';
  const isProOrBusiness = ['pro', 'business'].includes(userPlan);

  const MAX_FILE_SIZE_MB = PLAN_MAX_FILE_SIZE_MB[userPlan] || PLAN_MAX_FILE_SIZE_MB.free;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
  const isPaidPlan = ['starter', 'pro', 'business'].includes(userPlan);
  const PAID_ONLY_VALUES = ['15', '30', 'never'];

  const resetUpload = () => {
    setUploadState(STATE.IDLE);
    setProgress(0);
    setSelectedFile(null);
    setCustomFilename('');
    setOverwrite(false);
    setRenamedNotice('');
    setResultUrl('');
    setExpiresAt('');
    setRetentionDays(1);
    setErrorMessage('');
    setCopied(false);
    setExpiryDays('1');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getRetentionLabel = (days) => {
    if (days === null || days === undefined || days === 'never') return 'Permanent retention';
    const numDays = Number(days);
    if (numDays === 1) return '1-day retention';
    if (numDays === 365) return '1-year retention';
    return `${numDays}-day retention`;
  };

  const formatExpiry = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const validateFile = (file) => {
    if (!file) return 'Please select a file.';
    if (file.size > MAX_FILE_SIZE) {
      return 'file_too_large';
    }
    const DANGEROUS_EXTENSIONS = new Set([
      'exe', 'bat', 'cmd', 'com', 'pif', 'scr', 'vbs', 'jar', 'msi', 'dll', 'sh', 'app', 'deb', 'rpm', 'zip'
    ]);
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (DANGEROUS_EXTENSIONS.has(ext)) {
      return `File type '.${ext}' is not allowed for security reasons.`;
    }
    return null;
  };

  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setErrorMessage(error);
      setUploadState(STATE.ERROR);
      return;
    }
    setSelectedFile(file);
    setCustomFilename(file.name);
    setErrorMessage('');
    setUploadState(STATE.IDLE);
  };

  const onFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  }, []);

  const uploadAuthenticated = async (file) => {
    const token = await getToken();

    // Fetch user's dashboard data to get an active API key
    const dashRes = await fetch(`${API}/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!dashRes.ok) throw new Error('Failed to fetch account data.');
    const dashData = await dashRes.json();

    const activeKey = (dashData.user?.api_keys || []).find((k) => k.is_active);
    if (!activeKey) throw new Error('No active API key found. Please generate one from your dashboard.');

    const currentPlan = dashData.user?.plan || userPlan;

    if (currentPlan === 'free') {
      if (expiryDays === 'never') {
        throw new Error("Free plan only supports file retention up to 7 days. To use 'never' expiry, please upgrade your plan at https://uploadtourl.com/billing");
      }
      const days = parseInt(expiryDays, 10);
      if (!isNaN(days) && days > 7) {
        throw new Error(`Free plan only supports file retention up to 7 days (you requested ${days} days). To use longer retention, please upgrade your plan at https://uploadtourl.com/billing`);
      }
    }

    const isEligibleForCustomFilename = ['pro', 'business'].includes(currentPlan);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('expiry_days', expiryDays);
    if (source) formData.append('source', source);

    if (isEligibleForCustomFilename && customFilename && customFilename.trim()) {
      formData.append('custom_filename', customFilename.trim());
      if (overwrite) formData.append('overwrite', 'true');
    }

    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const res = JSON.parse(xhr.responseText);
          if (res.was_renamed) {
            setRenamedNotice(`File already existed in your folder and was saved as "${res.filename}".`);
          } else {
            setRenamedNotice('');
          }
          resolve(res);
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.detail || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.open('POST', `${API}/upload`);
      xhr.setRequestHeader('x-api-key', activeKey.key);
      xhr.send(formData);
    });
  };

  const uploadAnonymous = async (file) => {
    const tokenRes = await fetch(`${API}/public-upload/token`);
    if (!tokenRes.ok) {
      if (tokenRes.status === 429) {
        setUploadState(STATE.LIMIT_REACHED);
        return null;
      }
      throw new Error('Failed to prepare upload. Please try again.');
    }
    const { token } = await tokenRes.json();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('expiry_days', expiryDays);
    formData.append('upload_token', token);
    if (source) formData.append('source', source);

    const xhr = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else if (xhr.status === 429) {
          setUploadState(STATE.LIMIT_REACHED);
          resolve(null);
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(new Error(err.detail || `Upload failed (${xhr.status})`));
          } catch {
            reject(new Error(`Upload failed (${xhr.status})`));
          }
        }
      });
      xhr.addEventListener('error', () => reject(new Error('Network error')));
      xhr.open('POST', `${API}/public-upload`);
      xhr.send(formData);
    });
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Client-side retention validation for free plan / anonymous users before attempting upload
    if (!isSignedIn || userPlan === 'free') {
      if (expiryDays === 'never') {
        const msg = "Free plan only supports file retention up to 7 days. To use 'never' expiry, please upgrade your plan at https://uploadtourl.com/billing";
        setErrorMessage(msg);
        setUploadState(STATE.ERROR);
        toast.error(msg);
        return;
      }
      const days = parseInt(expiryDays, 10);
      if (!isNaN(days) && days > 7) {
        const msg = `Free plan only supports file retention up to 7 days (you requested ${days} days). To use longer retention, please upgrade your plan at https://uploadtourl.com/billing`;
        setErrorMessage(msg);
        setUploadState(STATE.ERROR);
        toast.error(msg);
        return;
      }
    }

    setUploadState(STATE.UPLOADING);
    setProgress(0);
    setErrorMessage('');

    try {
      let result;
      if (isSignedIn) {
        result = await uploadAuthenticated(selectedFile);
      } else {
        result = await uploadAnonymous(selectedFile);
      }

      if (!result) return; // Limit reached, state handled in uploadAnonymous

      setResultUrl(result.url);
      setExpiresAt(result.expires_at || '');
      if (result.retention_days !== undefined && result.retention_days !== null) {
        setRetentionDays(result.retention_days);
      } else if (expiryDays === 'never') {
        setRetentionDays('never');
      } else {
        setRetentionDays(parseInt(expiryDays, 10) || 1);
      }
      setUploadState(STATE.SUCCESS);
      toast.success('File uploaded successfully!');
    } catch (err) {
      setErrorMessage(err.message || 'Upload failed');
      setUploadState(STATE.ERROR);
      toast.error(err.message || 'Upload failed');
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(resultUrl);
    setCopied(true);
    toast.success('URL copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // --- Render states ---

  if (uploadState === STATE.SUCCESS) {
    return (
      <div className="bg-card border border-border rounded-2xl p-4 sm:p-6 shadow-card w-full overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full bg-success-bg flex items-center justify-center">
            <Check className="w-4 h-4 text-success" />
          </div>
          <p className="text-success font-semibold">Upload successful!</p>
          <button onClick={resetUpload} className="ml-auto p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {renamedNotice && (
          <div className="bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs p-2.5 rounded-lg mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>{renamedNotice}</span>
          </div>
        )}

        <div className="bg-background rounded-xl p-3 mb-3 w-full border border-border">
          <p className="text-xs text-muted-foreground mb-1">Your public URL</p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <code className="text-xs sm:text-sm text-brand break-all flex-1 font-mono min-w-0 p-2 sm:p-0 bg-brand-tint sm:bg-transparent rounded sm:rounded-none">{resultUrl}</code>
            <button
              onClick={copyUrl}
              className="flex-shrink-0 flex items-center justify-center gap-2 p-2 rounded-lg bg-brand hover:bg-brand/90 text-brand-foreground transition-colors w-full sm:w-auto mt-2 sm:mt-0"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="sm:hidden text-sm">Copy URL</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground text-center sm:text-left">
          <span>
            {expiresAt
              ? `Expires ${formatExpiry(expiresAt)} (${getRetentionLabel(retentionDays)})`
              : 'No expiry — this file will never be automatically deleted'}
          </span>
          {!isSignedIn && (
            <SignUpButton mode="modal">
              <button className="text-brand hover:opacity-80 font-semibold transition-opacity">
                Sign up for longer retention
              </button>
            </SignUpButton>
          )}
        </div>
      </div>
    );
  }

  if (uploadState === STATE.LIMIT_REACHED) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6 shadow-card w-full text-center">
        <div className="w-12 h-12 rounded-full bg-brand-tint flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-6 h-6 text-brand" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Free Upload Limit Reached</h3>
        <p className="text-brand font-semibold">Free upload used</p>
        <p className="text-xs text-muted-foreground mt-2 mb-6">
          You've used your free upload. Sign up for a free account to get <span className="text-foreground font-semibold">15 uploads/month</span> and manage your files.
        </p>
        <SignUpButton mode="modal">
          <Button className="w-full bg-brand hover:bg-brand/90 text-brand-foreground rounded-full py-5 font-bold transition-all hover:shadow-glow">
            Sign up free - get 15 uploads
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </SignUpButton>
        <button
          onClick={resetUpload}
          className="text-xs text-muted-foreground hover:text-foreground mt-3 font-medium transition-colors"
        >
          Try another file
        </button>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
        className={`relative bg-card border-2 border-dashed rounded-2xl p-6 sm:p-8 text-center transition-all ${
          isDragging
            ? 'border-brand bg-brand-tint shadow-glow scale-[1.01]'
            : 'border-border hover:border-brand/50 hover:bg-accent/50'
        } ${selectedFile ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={onFileInputChange}
          className="hidden"
        />

        {uploadState === STATE.UPLOADING ? (
          <div className="py-4">
            <div className="w-12 h-12 rounded-full bg-brand-tint flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Upload className="w-6 h-6 text-brand" />
            </div>
            <p className="text-sm font-semibold text-foreground mb-1">Uploading your file...</p>
            <p className="text-xs text-muted-foreground mb-4">{selectedFile?.name}</p>
            <div className="max-w-xs mx-auto space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-muted-foreground font-mono">{progress}%</p>
            </div>
          </div>
        ) : (
          <>
            {/* Drag & drop zone */}
            {!selectedFile && (
              <div>
                <div className="w-12 h-12 rounded-full bg-brand-tint flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-6 h-6 text-brand" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground mb-1">
                  Drop your file here, or <span className="text-brand">browse</span>
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Supports images, documents, audio, video & more (Up to {MAX_FILE_SIZE_MB}MB)
                </p>
                <div className="flex flex-wrap items-center justify-center gap-1.5 opacity-60">
                  {['.png', '.jpg', '.pdf', '.mp4', '.csv', '.mp3', '.svg', '.json', '.docx'].map((ext) => (
                    <span key={ext} className="text-[10px] font-mono px-2 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                      {ext}
                    </span>
                  ))}
                  <span className="text-[11px] text-muted-foreground font-semibold ml-0.5">& many more</span>
                </div>
              </div>
            )}

            {/* Selected file preview + upload button */}
            {selectedFile && (
              <div className="border-t border-border pt-4 mt-4 text-left">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); resetUpload(); }}
                    className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-2 mb-4">
                  {isSignedIn
                    ? 'Uploading with your account'
                    : <>7-day limit &middot; <span className="text-foreground/70">1 free upload without signup</span></>
                  }
                </p>

                {/* File Retention Selection */}
                <div className="flex items-center justify-between gap-4 mb-3 p-3 bg-background rounded-xl border border-border shadow-soft">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-brand" />
                    <Label className="text-xs font-semibold text-foreground/80">File Retention</Label>
                    <TooltipProvider delayDuration={150}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[200px] text-center">
                          {expiryDays === 'never'
                            ? 'Your file will never be automatically deleted.'
                            : `Your file will be automatically deleted after ${expiryDays} day${expiryDays === '1' ? '' : 's'}.`}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={expiryDays} onValueChange={setExpiryDays}>
                    <SelectTrigger className="w-[120px] h-8 text-xs bg-transparent border-border focus:ring-1 focus:ring-brand font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Day</SelectItem>
                      <SelectItem value="2">2 Days</SelectItem>
                      <SelectItem value="3">3 Days</SelectItem>
                      <SelectItem value="7">7 Days</SelectItem>
                      <SelectItem value="15">15 Days</SelectItem>
                      <SelectItem value="30">30 Days</SelectItem>
                      <SelectItem value="90">90 Days</SelectItem>
                      <SelectItem value="365">1 Year</SelectItem>
                      <SelectItem value="never">Never</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom File Name Section (Optional) */}
                <div className="mb-4 p-3 bg-background rounded-xl border border-border shadow-soft">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-brand" />
                      <Label className="text-xs font-semibold text-foreground/80">
                        File Name <span className="text-[11px] font-normal text-muted-foreground ml-0.5">(Optional)</span>
                      </Label>
                    </div>
                    {isProOrBusiness ? (
                      <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20">
                        PRO
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                        <Lock className="w-2.5 h-2.5" /> PRO ONLY
                      </span>
                    )}
                  </div>

                  {isProOrBusiness ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={customFilename}
                        onChange={(e) => setCustomFilename(e.target.value)}
                        placeholder="e.g. my-presentation.pdf"
                        className="w-full h-8 px-3 text-xs bg-muted/40 rounded-lg border border-border focus:outline-none focus:ring-1 focus:ring-brand font-medium text-foreground"
                      />
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={overwrite}
                          onChange={(e) => setOverwrite(e.target.checked)}
                          className="w-3.5 h-3.5 rounded border-border text-brand focus:ring-brand accent-brand"
                        />
                        <span className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
                          Overwrite if file already exists
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg border border-border/50">
                      <span className="text-[11px]">Choose a custom name for your file link</span>
                      <a
                        href="/billing"
                        className="text-[11px] font-bold text-brand hover:underline flex items-center gap-1"
                      >
                        Upgrade <Sparkles className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleUpload}
                  className="w-full bg-brand hover:bg-brand/90 text-brand-foreground rounded-full py-5 font-bold transition-all hover:shadow-glow"
                >
                  Upload & get URL
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Error message */}
            {uploadState === STATE.ERROR && errorMessage && (
              <div className="border-t border-border pt-3 mt-2">
                {errorMessage === 'file_too_large' ? (
                  <div className="flex items-start gap-2 text-left bg-destructive/10 border border-destructive/20 p-3 rounded-xl">
                    <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-destructive">
                        File exceeds {MAX_FILE_SIZE_MB}MB limit on your {userPlan.toUpperCase()} plan
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Upgrade your plan to upload larger files.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-xs text-destructive bg-destructive/10 p-2.5 rounded-lg border border-destructive/20 text-center">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {errorMessage.includes('https://uploadtourl.com/billing') ? (
                        <>
                          {errorMessage.split('https://uploadtourl.com/billing')[0]}
                          <a href="/billing" className="underline font-semibold hover:opacity-80">
                            https://uploadtourl.com/billing
                          </a>
                          {errorMessage.split('https://uploadtourl.com/billing')[1]}
                        </>
                      ) : (
                        errorMessage
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
